import { useCallback, useEffect, useState } from "react";
import { CombinedGraphQLErrors } from "@apollo/client";
import { useParams, useRouter, useSearchParams } from "next/navigation";

import { bootstrap } from "@/components/editor/bootstrap";
import ConfirmOverwriteAnimationModal from "@/components/modal/components/confirm-overwrite-animation";
import LoadingModal from "@/components/modal/components/loading";
import * as animationActions from "@/lib/state/actions/animation-actions";
import * as editorActions from "@/lib/state/actions/editor-actions";
import { AnimationState } from "@/lib/state/reducers/animation-default-state";
import { EDITOR_DB_STATE } from "@/lib/state/reducers/editor-reducer";
import { RootState, useAppDispatch } from "@/lib/state/store";

import { useModal } from "../use-modal";

export enum BOOTSTRAP_ERRORS {
  NOT_FOUND = "NOT_FOUND",
  LOAD_FAIL = "LOAD_FAIL",
}

type BootstrapSuccessCallback = (
  data?: Omit<AnimationState, "savedLocally" | "folderId"> | null,
) => void;

const useEditorBootstrap = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { closeModal, openModal } = useModal();
  const { animationUrl = null } = useParams<{ animationUrl: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<BOOTSTRAP_ERRORS>();
  const searchParams = useSearchParams();

  const onSuccess: BootstrapSuccessCallback = useCallback(
    (data, opts = { fromServer: false }) => {
      if (data) {
        dispatch(
          animationActions.set({
            ...data,
            savedLocally: false,
            folderId: null,
          }),
        );
        dispatch(editorActions.resetEditor());
        dispatch(editorActions.resetEditorLayers());
        if (opts.fromServer) {
          dispatch(animationActions.setSaved(true));
        }
        if (data.url && data.url !== animationUrl)
          router.replace(`/editor/${data.url}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`);
      }
      setLoading(false);
      closeModal();
    },
    [animationUrl, closeModal, dispatch, router, searchParams],
  );

  const onError = useCallback(
    (error: CombinedGraphQLErrors | unknown) => {
      if (CombinedGraphQLErrors.is(error) && error.errors) {
        if (error?.errors[0]?.extensions?.code === "NOT_FOUND") {
          setError(BOOTSTRAP_ERRORS.NOT_FOUND);
          closeModal();
        } else {
          closeModal();
          router.replace(`/editor/load-error/${animationUrl}`);
        }
      } else {
        closeModal();
        router.replace(`/editor/load-error/${animationUrl}`);
      }
      setLoading(false);
    },
    [animationUrl, closeModal, router],
  );

  const onIDBDetermined = useCallback(
    (isCompatible: boolean) => {
      dispatch(
        editorActions.setDbState(
          isCompatible
            ? EDITOR_DB_STATE.SUPPORTED
            : EDITOR_DB_STATE.UNSUPPORTED,
        ),
      );
    },
    [dispatch],
  );

  const onLoadProgress = useCallback(
    ({ resolved, total }: { resolved: number; total: number }) => {
      openModal(() => <LoadingModal resolved={resolved ?? 0} total={total} />);
    },
    [openModal],
  );

  const onUrlChange = useCallback(
    (url: string | null) => {
      if (url && url !== animationUrl) router.replace(`/editor/${url}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`);
    },
    [animationUrl, router, searchParams],
  );

  const onOverwritePrompt = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      openModal(() => (
        <ConfirmOverwriteAnimationModal
          onClearAndLoadSelected={() => resolve(true)}
          onCancelSelected={() => resolve(false)}
        />
      ));
    });
  }, [openModal]);

  useEffect(() => {
    setLoading(true);
    bootstrap({
      onSuccess,
      onError,
      onIDBDetermined,
      onLoadProgress,
      onOverwritePrompt,
      onUrlChange,
      animationUrl,
    });
  }, [
    animationUrl,
    onError,
    onIDBDetermined,
    onLoadProgress,
    onOverwritePrompt,
    onSuccess,
    onUrlChange,
  ]);

  return { loading, error };
};

export { useEditorBootstrap };
